import React, { useEffect, useRef } from "react";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import { Avatar, Box, Stack, Typography } from "@mui/material";

import { CARD_ASPECT_RATIO_CSS } from "@/shared/constants/cardLayout";
import { toPng } from "html-to-image";

import { isImpactCardType } from "@/cards/impact-card/utils/impactCardType";
import CardBorder from "@/modules/borders/CardBorder";
import { DeckCardDisplayData } from "../../../types/app-deck/deckCardThumbnail.types";

export type ImpactViewerRole = "creator" | "contributor" | "partner";

type DeckCardFrontFaceProps = {
  display: DeckCardDisplayData;
  compact?: boolean;
  isSealed?: boolean;
  addBorder?: boolean;
  viewerRole?: ImpactViewerRole | null;
  purchaseId?: number | string | null;
  /**
   * When this number changes (and is not 0),
   * the card front is saved as a PNG image.
   */
  downloadTrigger?: number;
  /** Called when the download finishes (success or error). */
  onDownloadComplete?: () => void;
};

const DeckCardFrontFace: React.FC<DeckCardFrontFaceProps> = ({
  display,
  compact = false,
  isSealed = false,
  addBorder = false,
  viewerRole,
  purchaseId,
  downloadTrigger = 0,
  onDownloadComplete,
}) => {
  // Ref points at the card DOM node we want to turn into an image
  const cardRef = useRef<HTMLDivElement>(null);

  // Keep the latest complete callback without re-running the download effect
  const onDownloadCompleteRef = useRef(onDownloadComplete);
  onDownloadCompleteRef.current = onDownloadComplete;

  // When downloadTrigger changes, save the card as a PNG
  useEffect(() => {
    // 0 means the user has not clicked Download yet
    if (!downloadTrigger) {
      return;
    }

    const cardElement = cardRef.current;
    if (!cardElement) {
      onDownloadCompleteRef.current?.();
      return;
    }

    const saveCardImage = async () => {
      try {
        // Turn the card HTML into a PNG image
        const imageUrl = await toPng(cardElement, { cacheBust: true });

        // Create a temporary link and click it to download
        const link = document.createElement("a");
        link.download = `${display.title || "card"}.png`;
        link.href = imageUrl;
        link.click();
      } catch (error) {
        console.error("Could not download card image", error);
      } finally {
        // Tell the parent the download attempt is done
        onDownloadCompleteRef.current?.();
      }
    };

    void saveCardImage();
  }, [downloadTrigger, display.title]);

  const heroImage = display.imageUrl || display.coverImageUrl;
  const hideCreatorChrome =
    isImpactCardType(display.type) || isImpactCardType(display.typeLabel);

  const cardRadius = compact
    ? { xs: "12px", sm: "14px", md: "16px" }
    : { xs: "14px", sm: "16px", md: "18px" };

  const avatarSize = compact ? { xs: 26, sm: 26, md: 30 } : { xs: 30, sm: 34, md: 38 };

  const titleSize = compact
    ? {
        xs: "0.8rem",
        sm: "1rem",
        md: "1.25rem",
      }
    : {
        xs: "1.2rem",
        sm: "1.6rem",
        md: "2rem",
      };

  const typeSize = compact
    ? {
        xs: "0.42rem",
        sm: "0.5rem",
        md: "0.58rem",
      }
    : {
        xs: "0.58rem",
        sm: "0.68rem",
        md: "0.8rem",
      };

  const statusSize = compact
    ? {
        xs: "0.4rem",
        sm: "0.46rem",
        md: "0.55rem",
      }
    : {
        xs: "0.55rem",
        sm: "0.65rem",
        md: "0.8rem",
      };

  const Frontface = () => {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",

          // Keep the original compact behavior.
          height: compact ? "auto" : "100%",

          aspectRatio: CARD_ASPECT_RATIO_CSS,

          flexShrink: 0,
          overflow: "hidden",

          borderRadius: cardRadius,

          border: {
            xs: "1px solid var(--primary-600)",
            sm: "1px solid var(--primary-600)",
          },

          boxSizing: "border-box",
          bgcolor: "var(--neutralBlack)",
          isolation: "isolate",

          boxShadow: compact ? "var(--shadow-sm)" : "var(--shadow-md)",
        }}
      >
        {/* Background image */}
        {heroImage ? (
          <Box
            component="img"
            src={heroImage}
            alt={display.title}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "var(--brown-900)",
            }}
          />
        )}

        {/* Dark overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",

            background: `
                linear-gradient(
                  180deg,
                  var(--neutral-800) 0%,
                  transparent 25%,
                  transparent 48%,
                  var(--neutralBlack) 100%
                )
              `,

            opacity: 0.7,
          }}
        />

        {/* Bottom dark gradient */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,

            height: compact ? "48%" : "45%",

            background: `
                linear-gradient(
                  180deg,
                  transparent 0%,
                  var(--neutralBlack) 100%
                )
              `,

            opacity: 0.95,
            pointerEvents: "none",
          }}
        />

        {/* Top content — Impact Cards stay cause-first, no creator chrome */}
        {!hideCreatorChrome ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            position: "absolute",
            top: compact ? { xs: "4%", sm: "4.5%" } : { xs: "4%", sm: "4.5%" },
            left: compact ? { xs: "4%", sm: "4.5%" } : { xs: "4%", sm: "4.5%" },
            right: compact ? { xs: "4%", sm: "4.5%" } : { xs: "4%", sm: "4.5%" },
            zIndex: 2,
            gap: 1,
          }}
        >
          {/* Talent */}
          {display.talent && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={compact ? { xs: 0.3, sm: 0.5, md: 0.6 } : 1}
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Avatar
                src={display.talent.profile_image || undefined}
                alt={display.talent.username || "Talent"}
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  flexShrink: 0,
                  border: "1px solid var(--primary-600)",
                }}
              />

              <Box
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "var(--neutralWhite)",
                    fontFamily: "var(--primary-font-family)",

                    fontSize: compact
                      ? {
                          xs: "0.5rem",
                          sm: "0.5rem",
                          md: "0.58rem",
                        }
                      : {
                          xs: "0.72rem",
                          sm: "0.85rem",
                          md: "1rem",
                        },

                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",

                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {display.talent.username || "Talent"}
                </Typography>

                <Typography
                  sx={{
                    mt: compact ? 0.1 : 0.4,

                    color: "var(--neutral-100)",
                    fontFamily: "var(--primary-font-family)",

                    fontSize: compact
                      ? {
                          xs: "0.5rem",
                          sm: "0.5rem",
                          md: "0.54rem",
                        }
                      : {
                          xs: "0.58rem",
                          sm: "0.68rem",
                          md: "0.78rem",
                        },

                    fontWeight: 400,
                    lineHeight: 1,
                  }}
                >
                  Talent
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Living / Sealed */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              minWidth: compact
                ? {
                    xs: "58px",
                    sm: "68px",
                    md: "78px",
                  }
                : {
                    xs: "68px",
                    sm: "82px",
                    md: "104px",
                  },

              height: compact
                ? {
                    xs: "20px",
                    sm: "23px",
                    md: "26px",
                  }
                : {
                    xs: "24px",
                    sm: "27px",
                    md: "30px",
                  },

              px: compact
                ? {
                    xs: 0.7,
                    sm: 1,
                    md: 1.2,
                  }
                : 1.5,

              borderRadius: "999px",

              bgcolor: "var(--neutral-800)",
              border: "1px solid var(--neutral-400)",

              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--primary-font-family)",

                fontSize: statusSize,

                fontWeight: 700,

                letterSpacing: compact
                  ? {
                      xs: "0.06em",
                      sm: "0.08em",
                      md: "0.1em",
                    }
                  : {
                      xs: "0.08em",
                      sm: "0.1em",
                      md: "0.12em",
                    },

                lineHeight: 1,

                color: isSealed ? "var(--destructive-600)" : "var(--secondary-600)",
              }}
            >
              {isSealed ? "SEALED" : "LIVING"}
            </Typography>
          </Box>
        </Stack>
        ) : null}

        {/* Bottom content */}
        <Box
          sx={{
            position: "absolute",
            left: compact ? { xs: "5%", sm: "5%", md: "5%" } : { xs: "5%", sm: "5%", md: "4.8%" },
            right: compact ? { xs: "5%", sm: "5%", md: "5%" } : { xs: "5%", sm: "5%", md: "4.8%" },
            bottom: compact ? { xs: "5%", sm: "5%", md: "5%" } : { xs: "5%", sm: "5%", md: "5%" },
            zIndex: 2,
            minWidth: 0,
          }}
        >
          {/* Card type */}
          <Typography
            sx={{
              color: "var(--primary-600)",
              fontFamily: "var(--primary-font-family)",

              fontSize: typeSize,

              fontWeight: 700,

              letterSpacing: compact
                ? {
                    xs: "0.07em",
                    sm: "0.08em",
                    md: "0.1em",
                  }
                : {
                    xs: "0.08em",
                    sm: "0.1em",
                    md: "0.12em",
                  },

              textTransform: "uppercase",
              lineHeight: 1.2,

              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {display.typeLabel || "SINGLE ALBUM"}
          </Typography>

          {/* Gold line */}
          <Box
            sx={{
              width: compact
                ? {
                    xs: "40px",
                    sm: "50px",
                    md: "60px",
                  }
                : {
                    xs: "48px",
                    sm: "62px",
                    md: "76px",
                  },

              height: "2px",
              mt: compact
                ? {
                    xs: "4px",
                    sm: "5px",
                  }
                : "6px",
              mb: compact
                ? {
                    xs: "5px",
                    sm: "7px",
                  }
                : "9px",
              bgcolor: "var(--primary-600)",
            }}
          />

          {/* Title */}
          <Typography
            sx={{
              color: "var(--neutralWhite)",
              fontFamily: '"Playfair Display", serif',
              fontSize: titleSize,
              fontWeight: 500,
              lineHeight: 1.05,
              letterSpacing: "0.01em",
              textTransform: "uppercase",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "wrap",
              maxWidth: viewerRole ? "82%" : "100%",
            }}
          >
            {display.title}
          </Typography>
        </Box>

        {viewerRole ? (
          <Box
            aria-label={
              viewerRole === "creator"
                ? "Creator"
                : viewerRole === "partner"
                  ? "Partner"
                  : "Contributor"
            }
            sx={{
              position: "absolute",
              right: compact ? { xs: "4%", sm: "4.5%" } : { xs: "4%", sm: "4.5%" },
              bottom: compact ? { xs: "4%", sm: "4.5%" } : { xs: "4%", sm: "4.5%" },
              zIndex: 3,
              width: compact
                ? { xs: 22, sm: 24, md: 28 }
                : { xs: 28, sm: 32, md: 36 },
              height: compact
                ? { xs: 22, sm: 24, md: 28 }
                : { xs: 28, sm: 32, md: 36 },
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor:
                viewerRole === "creator"
                  ? "rgba(16, 44, 36, 0.92)"
                  : viewerRole === "partner"
                    ? "rgba(220, 234, 223, 0.95)"
                    : "rgba(255, 255, 255, 0.92)",
              border:
                viewerRole === "creator"
                  ? "1px solid rgba(255,255,255,0.35)"
                  : "1px solid rgba(94, 143, 114, 0.55)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
            }}
          >
            {viewerRole === "creator" ? (
              <AutoAwesomeRoundedIcon
                sx={{
                  fontSize: compact ? "0.78rem" : "1.05rem",
                  color: "#FFFFFF",
                }}
              />
            ) : viewerRole === "partner" ? (
              <HandshakeOutlinedIcon
                sx={{
                  fontSize: compact ? "0.78rem" : "1.05rem",
                  color: "#102C24",
                }}
              />
            ) : (
              <VolunteerActivismOutlinedIcon
                sx={{
                  fontSize: compact ? "0.78rem" : "1.05rem",
                  color: "#1D4437",
                }}
              />
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <Box ref={cardRef} sx={{ width: "100%", height: "100%" }}>
      {addBorder ? (
        <Box sx={{ borderRadius: cardRadius }}>
          <CardBorder>
            <Frontface />
          </CardBorder>
        </Box>
      ) : (
        <Frontface />
      )}
    </Box>
  );
};

export default DeckCardFrontFace;
